import { call, put, takeLatest, select, } from 'redux-saga/effects';
import { salesDemoActions } from './slice';
import {
    fetchMetrics,
    fetchTimeperiod,
    fetchDataGridData,
    fetchNurturingHistory,
    fetchTaskHtml,
    convertJsonObjects
} from 'app/pages/SalesPortalPage/LayoutV1/data/salesDemoDataService';
import { RootState } from 'types/RootState';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import { act } from 'react-test-renderer';

const selectUseMockData = (state: RootState) => state.salesDemoState.mockApiFlag;
const selectUseMysql = (state: RootState) => state.salesDemoState.mysqlFlag;
const selectExcludeMysqlParams = (state: RootState) => state.salesDemoState.excludeMysqlParams;

function* toggleUseMockDataSaga(
    action: ReturnType<typeof salesDemoActions.toggleUseMockData>,
) {
    // console.log('Mock data flag set to:', action.payload);
}

function* fetchMetricsSaga(action: ReturnType<any>) {
    try {
        const useMockData = yield select(selectUseMockData);
        const useMysql = yield select(selectUseMysql);
        const excludeMysqlParams = yield select(selectExcludeMysqlParams);

        // Check if 'metrics' is in the excluded params list
        const shouldExcludeMysql = excludeMysqlParams.includes('metrics');

        const response = yield call(fetchMetrics, {
            dealershipId: action.payload.dealershipId,
            timeperiod: action.payload.timeperiod,
            startDate: action.payload.startDate,
            endDate: action.payload.endDate,
            conversation_id: action.payload.conversation_id,
            useMockData,
            useMysql: shouldExcludeMysql ? undefined : useMysql
        });
        yield put(salesDemoActions.requestMetricsSuccess(response));
    } catch (error: any) {
        console.log(error);
        yield put(salesDemoActions.requestMetricsError(error.message));
        yield put(notificationAlertActions.addNotification({
            open: true, message: 'failed to fetch sales metrics', severity: 'error'
        }));
    }
}

function* fetchTimeperiodSaga(action: ReturnType<any>) {
    try {
        const useMockData = yield select(selectUseMockData);
        const excludeMysqlParams = yield select(selectExcludeMysqlParams);

        // Check if 'timeperiod' is in the excluded params list
        const shouldExcludeMysql = excludeMysqlParams.includes('timeperiod');

        const response = yield call(fetchTimeperiod, {
            useMockData,
        });
        yield put(salesDemoActions.requestTimeperiodSuccess(response));
    } catch (error: any) {
        console.log(error);
        yield put(salesDemoActions.requestTimeperiodError(error.message));
        yield put(notificationAlertActions.addNotification({
            open: true, message: 'failed to fetch timeperiod', severity: 'error'
        }));
    }
}

function* fetchDataGridDataSaga(action: ReturnType<any>) {
    try {
        const useMockData = yield select(selectUseMockData);
        const useMysql = yield select(selectUseMysql);
        const excludeMysqlParams = yield select(selectExcludeMysqlParams);

        // Check if 'dataGrid' is in the excluded params list
        const shouldExcludeMysql = excludeMysqlParams.includes('dataGrid');

        const filterType = action.payload?.filterType || null;
        const dealershipId = action.payload.dealershipId || null;
        const nurturingStatus = action.payload.nurturingStatus || null;
        const timeperiod = action.payload.timeperiod || null;
        const groupId = action.payload.groupId || null;
        const data = action.payload.data || null;
        const response = yield call(fetchDataGridData, {
            useMockData,
            useMysql: shouldExcludeMysql ? undefined : useMysql,
            filterType,
            dealershipId,
            nurturingStatus,
            timeperiod,
            groupId,
            data
        });
        yield put(salesDemoActions.requestDataGridDataSuccess(response));
    } catch (error: any) {
        console.log(error);
        yield put(salesDemoActions.requestDataGridDataError(error.message));
        yield put(notificationAlertActions.addNotification({
            open: true, message: 'Oops! something went wrong', severity: 'error'
        }));
    }
}

function* fetchNurturingHistorySaga(action: ReturnType<any>) {
    try {
        const useMockData = yield select(selectUseMockData);
        const useMysql = yield select(selectUseMysql);
        const excludeMysqlParams = yield select(selectExcludeMysqlParams);

        // Check if 'nurturingHistory' is in the excluded params list
        const shouldExcludeMysql = excludeMysqlParams.includes('nurturingHistory');

        const opportunityId = action.payload.opportunityId;
        const dealershipId = action.payload.dealershipId;

        const response = yield call(fetchNurturingHistory, {
            useMockData,
            useMysql: shouldExcludeMysql ? undefined : useMysql,
            opportunityId,
            dealershipId
        });
        yield put(salesDemoActions.requestNurturingHistorySuccess(response));
    } catch (error: any) {
        console.log(error);
        yield put(salesDemoActions.requestNurturingHistoryError(error.message));
        yield put(notificationAlertActions.addNotification({
            open: true, message: 'failed to fetch nurturing history', severity: 'error'
        }));
    }
}

function* fetchTaskHtmlSaga({ payload }: ReturnType<any>) {
    try {
        const useMockData = yield select(selectUseMockData);
        const excludeMysqlParams = yield select(selectExcludeMysqlParams);

        // Check if 'taskHtml' is in the excluded params list
        const shouldExcludeMysql = excludeMysqlParams.includes('taskHtml');

        const response = yield call(fetchTaskHtml, {
            data: payload.data,
            mysqlFlag: shouldExcludeMysql ? undefined : payload.mysqlFlag,
            useMockData,
        });

        // Process response to detect S3 URLs
        if (response?.body) {
            const processedResponse = { ...response };
            const processedBody: any = {};
            
            Object.keys(response.body).forEach(taskId => {
                if (response.body[taskId]) {
                    const responseValue = response.body[taskId];
                    
                    // Check if response is an S3 URL
                    const isS3Url = typeof responseValue === 'string' && 
                                   (responseValue.includes('amazonaws.com') || 
                                    responseValue.includes('.s3.') ||
                                    responseValue.match(/^https?:\/\/.*\.s3[.-].*\.amazonaws\.com/) ||
                                    responseValue.match(/^https?:\/\/s3[.-].*\.amazonaws\.com/) ||
                                    (responseValue.startsWith('http') && responseValue.includes('s3')));
                    
                    if (isS3Url) {
                        console.log(`SalesDemoPage: API returned S3 URL for task ${taskId}, will be handled by NurturingHistoryMessageContainer:`, responseValue);
                        // Don't store S3 URLs in taskHtml, let NurturingHistoryMessageContainer handle them
                        // The URL will be available in the original task data as s3_url
                    } else {
                        // Regular HTML content
                        processedBody[taskId] = responseValue;
                    }
                }
            });
            
            processedResponse.body = processedBody;
            yield put(salesDemoActions.requestTaskHtmlSuccess(processedResponse));
        } else {
            yield put(salesDemoActions.requestTaskHtmlSuccess(response));
        }
    } catch (error: any) {
        yield put(salesDemoActions.requestTaskHtmlError(error.message));
        yield put(notificationAlertActions.addNotification({
            open: true, message: 'failed to fetch message', severity: 'error'
        }));
    }
}

function* exportDataSaga({ payload }: ReturnType<any>) {
    try {
        const useMockData = yield select(selectUseMockData);
        const jsonObjects = payload.jsonObjects;
        const fileName = payload.fileName;

        const response = yield call(convertJsonObjects, {
            jsonObjects, fileName
        });

        yield put(salesDemoActions.requestExportDataSuccess(response));
    } catch (error: any) {
        yield put(salesDemoActions.requestExportDataError(error.message));
        yield put(notificationAlertActions.addNotification({
            open: true, message: 'failed to export data', severity: 'error'
        }));
    }
}

export function* watchFetchSalesDemo() {
    yield takeLatest(
        salesDemoActions.toggleUseMockData.type,
        toggleUseMockDataSaga,
    );
    yield takeLatest(salesDemoActions.requestMetrics.type, fetchMetricsSaga);
    yield takeLatest(salesDemoActions.requestTimeperiod.type, fetchTimeperiodSaga);
    yield takeLatest(salesDemoActions.requestDataGridData.type, fetchDataGridDataSaga);
    yield takeLatest(salesDemoActions.requestNurturingHistory.type, fetchNurturingHistorySaga);
    yield takeLatest(salesDemoActions.requestTaskHtml.type, fetchTaskHtmlSaga);
    yield takeLatest(salesDemoActions.requestExportData.type, exportDataSaga);
}
